import React, { useEffect, useState } from "react";

import shopifyLogo from "@assets/shopify-mini-logo.png";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  OnboardingStepType,
  type GetSteps,
  type UpdateStepMetadata,
  type UpdateStepMetadataVariables,
} from "@north-beam/nb-client-side";
import { type InstallationScriptDetails } from "@north-beam/nb-common";
import { GET_STEPS, UPDATE_STEP_METADATA } from "@pages/onboarding";
import { WarningPanel } from "@pages/onboarding/core/components/warning-panel";
import { NeedHelpWithPixel } from "@pages/onboarding/core/pixel-placement";

import { NorthbeamPixelTag } from "./northbeam-pixel-tag";
import { OnboardingTrackingScript } from "./onboarding-tracking-script";
import { ShopifyOrderStatusTag } from "./shopify-order-status-tag";
import { ShopifyPostPurchaseUpsellTag } from "./shopify-post-purchase-upsell-tag";

interface TrackingScriptTagsProps {
  installationScripts: InstallationScriptDetails;
}
export const TrackingScriptTags = ({
  installationScripts,
}: TrackingScriptTagsProps): React.ReactElement => {
  const [updateStepMetadataMutation] = useNorthbeamMutation<
    UpdateStepMetadata,
    UpdateStepMetadataVariables
  >(UPDATE_STEP_METADATA, {
    refetchQueries: [{ query: GET_STEPS }],
    doNotRaiseErrorOnFailure: true,
  });

  const { data: onboardingV2Steps, loading: isOnboardingV2StepsLoading } =
    useNorthbeamQuery<GetSteps>(GET_STEPS, {
      fetchPolicy: "network-only",
    });
  const pixelPlacementStepMetadata =
    onboardingV2Steps?.me.onboardingSteps?.find(
      (step) => step.step === "PIXEL_PLACEMENT",
    )?.step_metadata;

  const [stepMetadata, setStepMetadata] = useState(pixelPlacementStepMetadata);

  useEffect(() => {
    if (pixelPlacementStepMetadata && !stepMetadata) {
      setStepMetadata(pixelPlacementStepMetadata);
    }
  }, [pixelPlacementStepMetadata, stepMetadata]);

  const updateStepMetadata = (stepMetadata: any) => {
    setStepMetadata(stepMetadata);
    updateStepMetadataMutation({
      variables: {
        step: OnboardingStepType.PIXEL_PLACEMENT,
        data: stepMetadata,
      },
    });
  };
  return (
    <>
      <div className="pb-20 border-b border-gray-0 px-10 pt-10 scroll-m-10">
        <div className="text-slate-900 text-2xl font-semibold leading-loose pb-6">
          Please select your e-commerce platform
        </div>
        <div className="flex gap-6 flex-col">
          <div className="flex items-center gap-2">
            <input
              id="shopify"
              onChange={(e) => {
                if (e.target.checked) {
                  updateStepMetadata({
                    provider: "shopify",
                  });
                }
              }}
              checked={stepMetadata?.provider === "shopify"}
              type="radio"
            />
            <label className="flex items-center mb-0 gap-2" htmlFor="shopify">
              <img src={shopifyLogo} alt="shopify Logo" className="w-5 h-5" />
              <div>Shopify Pixel</div>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="headless"
              onChange={(e) => {
                if (e.target.checked) {
                  updateStepMetadata({
                    provider: "headless",
                  });
                }
              }}
              checked={stepMetadata?.provider === "headless"}
              type="radio"
            />
            <label className="flex items-center mb-0 gap-2" htmlFor="headless">
              <img src={shopifyLogo} alt="shopify Logo" className="w-5 h-5" />
              <div>
                We use Shopify for checkout, but have a custom site (Headless
                Solution)
              </div>
            </label>
          </div>
          <div className="flex items-start gap-2">
            <input
              id="other"
              onChange={(e) => {
                if (e.target.checked) {
                  updateStepMetadata({
                    provider: "other",
                  });
                }
              }}
              checked={stepMetadata?.provider === "other"}
              type="radio"
            />
            <label className="flex flex-col mb-0 gap-2 -mt-2" htmlFor="other">
              <div>
                Other e-commerce platform or custom solution (e.g Salesforce,
                Data Lakes)
              </div>
              <div className="text-sm font-semibold">
                Only available on Pro and Enterprise Plans
              </div>
            </label>
          </div>
        </div>
      </div>
      {stepMetadata?.provider === "shopify" && (
        <>
          <div className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 ">
            <div className="text-slate-900 text-2xl font-semibold leading-loose">
              Please install the pixel on every page of your site
            </div>
            <div className="w-full">
              <NorthbeamPixelTag installationScripts={installationScripts} />
              {stepMetadata?.provider === "shopify" && (
                <>
                  <ShopifyOrderStatusTag
                    installationScripts={installationScripts}
                  />
                  <ShopifyPostPurchaseUpsellTag
                    installationScripts={installationScripts}
                  />
                </>
              )}
            </div>
            <NeedHelpWithPixel link="https://docs.northbeam.io/docs/shopify-installation" />
          </div>
        </>
      )}
      {stepMetadata?.provider === "headless" && (
        <>
          <div className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 scroll-m-10">
            <div className="text-slate-900 text-2xl font-semibold leading-loose">
              Please install the pixel on every page of your site
            </div>
            <div className="w-full">
              <NorthbeamPixelTag installationScripts={installationScripts} />
              <ShopifyOrderStatusTag
                installationScripts={installationScripts}
              />
              <ShopifyPostPurchaseUpsellTag
                installationScripts={installationScripts}
              />
            </div>
            <NeedHelpWithPixel />
          </div>
        </>
      )}
      {stepMetadata?.provider === "other" && (
        <>
          <div className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 scroll-m-10">
            <div className="text-slate-900 text-2xl font-semibold leading-loose">
              Please install the pixel on every page of your site
            </div>
            <div className="flex flex-row gap-2 items-center">
              <div className="h-1 w-1 rounded-full bg-interstellar+0"></div>
              <div>
                You’ll need to install this script in the{" "}
                <code>{"<head>"}</code> section of every page on your site:
              </div>
            </div>
            <WarningPanel>
              <span className="text-lg font-semibold leading-normal">
                Do not change the script
              </span>
            </WarningPanel>
            <div className="w-full">
              <OnboardingTrackingScript
                displayName="Northbeam Pixel"
                header={
                  "Please place this pixel on every page of your website. This is " +
                  "Northbeam's primary pixel."
                }
                scriptCode={installationScripts.northbeam_pixel}
                descriptionHTML={
                  <>
                    <div className="flex flex-row gap-2 items-center">
                      <div className="h-1 w-1 rounded-full bg-interstellar+0"></div>
                      If you use Google Tag Manager, we recommend following
                      Northbeam’s{" "}
                      <a
                        href="https://docs.northbeam.io/docs/google-tag-manager-installation"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Google Tag Manager instructions
                      </a>
                    </div>
                  </>
                }
              />
            </div>
          </div>
          <div className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 scroll-m-10">
            <div className="text-slate-900 text-2xl font-semibold leading-loose">
              Please implement the 'firePurchaseEvent' Javascript function at
              conversion points
            </div>
            <ul className="list-disc pt-2 pl-6">
              <li className="pb-2">
                After embedding the pixel on every single page you will need to
                implement the <code>firePurchaseEvent</code> JavaScript function
                at every conversion point in your store.
              </li>
              <li className="pb-2">
                The <code>firePurchaseEvent</code> function at every conversion
                point in your store.
              </li>
              <li>
                The <code>firePurchaseEvent</code> data should match what you
                upload to Northbeam through the orders API.
              </li>
            </ul>
            <NeedHelpWithPixel
              link="https://docs.northbeam.io/docs/standard-installation#implement-firepurchaseevent-to-track-purchases"
              text="Need help setting up the 'firePurchaseEvent'?"
            />
          </div>
        </>
      )}
    </>
  );
};
